import type { InternalNetworkStatsDto } from '@terra/shared-contracts-internal-network';

export type InternalNetworkSignalSetupConnectionMode = 'sensor' | 'tunnel';

type SensorEnrollmentLike = { deploymentStatus: string };

type TunnelEnrollmentLike = { status: string; routesApproved?: boolean };

const isActiveSensorEnrollment = (enrollment: SensorEnrollmentLike) =>
  enrollment.deploymentStatus !== 'REVOKED';

const isActiveTunnelEnrollment = (enrollment: TunnelEnrollmentLike) =>
  enrollment.status !== 'REVOKED';

export const isInternalNetworkConnectionSetupComplete = ({
  enrollments,
  tunnelEnrollments,
  connectionMode,
}: {
  enrollments: SensorEnrollmentLike[] | undefined;
  tunnelEnrollments: TunnelEnrollmentLike[] | undefined;
  connectionMode: InternalNetworkSignalSetupConnectionMode;
}): boolean => {
  if (connectionMode === 'tunnel') {
    return (tunnelEnrollments ?? [])
      .filter(isActiveTunnelEnrollment)
      .some(
        (enrollment) =>
          enrollment.status === 'ACTIVE' && enrollment.routesApproved === true,
      );
  }

  return (enrollments ?? [])
    .filter(isActiveSensorEnrollment)
    .some((enrollment) => enrollment.deploymentStatus === 'ONLINE');
};

export const hasInternalNetworkImportedGraphData = (
  stats: InternalNetworkStatsDto,
): boolean =>
  stats.totalUsers +
    stats.totalGroups +
    stats.totalComputers +
    stats.totalOus +
    stats.totalDomains +
    stats.totalGpos +
    stats.totalDomainControllers +
    stats.totalCertTemplates +
    stats.totalContainers >
  0;

export const resolveInternalNetworkSignalSetupAvailable = ({
  stats,
  sensorSetupComplete,
}: {
  stats: InternalNetworkStatsDto | undefined;
  sensorSetupComplete: boolean;
}): boolean => {
  if (!sensorSetupComplete || !stats) {
    return false;
  }

  if (stats.latestDiscoveryStatus === 'COMPLETED') {
    return true;
  }

  return (
    hasInternalNetworkImportedGraphData(stats) ||
    stats.crownJewelCount > 0 ||
    stats.ownedPrincipalCount > 0 ||
    stats.signalCount > 0
  );
};
